
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function PharmacistResources() {
  const [activeTab, setActiveTab] = useState('specialty');

  const specialtyPharmacyContent = [
    {
      title: '専門医療機関連携薬局',
      description: 'がん、精神科、小児等の専門医療に対応する薬局の運営ガイド',
      features: ['専門医療機関との連携体制', '専門薬剤師の配置', '高度薬学管理', '患者フォローアップ'],
      icon: 'ri-hospital-line',
      color: 'bg-red-50 text-red-600'
    },
    {
      title: '地域連携薬局',
      description: '地域包括ケアシステムの中核を担う薬局の機能と役割',
      features: ['24時間対応体制', '在宅医療対応', '多職種連携', '地域医療連携'],
      icon: 'ri-community-line',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      title: '健康サポート薬局',
      description: '健康相談、健康づくりの拠点となる薬局の運営',
      features: ['健康相談対応', 'セルフケア支援', '健康情報提供', '受診勧奨'],
      icon: 'ri-heart-pulse-line',
      color: 'bg-green-50 text-green-600'
    }
  ];

  const sterilePreparationContent = [
    {
      title: 'クリーンベンチ導入',
      description: '無菌調剤に必要な設備導入と環境整備',
      features: ['設備選定基準', '設置環境要件', 'メンテナンス方法', 'コスト計算'],
      icon: 'ri-microscope-line',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      title: '無菌操作手順',
      description: '安全で確実な無菌調剤の手技と手順',
      features: ['手洗い・消毒手順', '無菌操作技術', '品質管理', '記録・文書化'],
      icon: 'ri-hand-sanitizer-line',
      color: 'bg-cyan-50 text-cyan-600'
    },
    {
      title: '注射薬取扱い',
      description: '薬局で扱える注射薬の種類と管理方法',
      features: ['取扱い可能薬剤', '保管・管理方法', '調剤技術', '安全対策'],
      icon: 'ri-syringe-line',
      color: 'bg-orange-50 text-orange-600'
    },
    {
      title: '処方箋チェック',
      description: '無菌調剤処方箋の確認ポイント',
      features: ['処方内容確認', '相互作用チェック', '投与量確認', '患者情報確認'],
      icon: 'ri-file-check-line',
      color: 'bg-pink-50 text-pink-600'
    }
  ];

  const homeCareContent = [
    {
      title: '在宅調剤業務',
      description: '在宅患者への調剤サービスの提供方法',
      features: ['訪問調剤', '服薬指導', '薬歴管理', '医師連携'],
      icon: 'ri-home-heart-line',
      color: 'bg-teal-50 text-teal-600'
    },
    {
      title: '多職種連携',
      description: '医師、看護師、介護士等との効果的な連携',
      features: ['連携体制構築', '情報共有方法', '役割分担', 'カンファレンス参加'],
      icon: 'ri-team-line',
      color: 'bg-indigo-50 text-indigo-600'
    },
    {
      title: '在宅医療機器',
      description: '在宅で使用する医療機器の管理とサポート',
      features: ['機器の種類', '使用方法指導', 'メンテナンス', 'トラブル対応'],
      icon: 'ri-stethoscope-line',
      color: 'bg-emerald-50 text-emerald-600'
    }
  ];

  const dxContent = [
    {
      title: 'オンライン服薬指導',
      description: 'オンライン服薬指導の実施方法と注意点',
      features: ['システム導入', '患者対応方法', '法的要件', '品質確保'],
      icon: 'ri-video-line',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      title: '電子処方箋対応',
      description: '電子処方箋システムの導入と運用',
      features: ['システム構築', '運用フロー', 'セキュリティ', 'トラブル対応'],
      icon: 'ri-file-text-line',
      color: 'bg-green-50 text-green-600'
    },
    {
      title: 'お薬ロッカー',
      description: '24時間対応可能なお薬ロッカーの導入',
      features: ['設置場所選定', '運用方法', '患者案内', 'メンテナンス'],
      icon: 'ri-safe-line',
      color: 'bg-yellow-50 text-yellow-600'
    },
    {
      title: '薬局DX推進',
      description: 'デジタル技術を活用した薬局業務の効率化',
      features: ['業務効率化', 'データ活用', 'AI導入', 'システム連携'],
      icon: 'ri-robot-line',
      color: 'bg-purple-50 text-purple-600'
    }
  ];

  const getContentByTab = (tab: string) => {
    switch (tab) {
      case 'specialty': return specialtyPharmacyContent;
      case 'sterile': return sterilePreparationContent;
      case 'homecare': return homeCareContent;
      case 'dx': return dxContent;
      default: return specialtyPharmacyContent;
    }
  };

  const currentContent = getContentByTab(activeTab);

  const additionalResources = [
    {
      title: '薬剤師豆知識',
      description: '日常業務で役立つ薬学知識とコツ',
      icon: 'ri-lightbulb-line',
      link: '/pharmacist-tips'
    },
    {
      title: '災害対策',
      description: 'モバイルファーマシー、DMAT、災害時薬剤師活動',
      icon: 'ri-shield-line',
      link: '/disaster-response'
    },
    {
      title: '2027年診療報酬改定',
      description: '最新の診療報酬改定情報と対応策',
      icon: 'ri-calendar-event-line',
      link: '/reimbursement-2027'
    },
    {
      title: '学会・研修情報',
      description: '薬剤師向け学会、研修会の最新情報',
      icon: 'ri-graduation-cap-line',
      link: '/conversations'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/6773d4d34080772d1b4af7a944e63df9.jpg"
            alt="薬剤師向けリソース"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            薬剤師向け<span className="text-teal-600">専門リソース</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            専門薬局運営から最新技術まで、薬剤師の専門性向上をサポート
          </p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => setActiveTab('specialty')}
              className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'specialty'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover-bg-teal-50 hover:text-teal-600'
              }`}
            >
              専門薬局
            </button>
            <button
              onClick={() => setActiveTab('sterile')}
              className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'sterile'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover-bg-teal-50 hover:text-teal-600'
              }`}
            >
              無菌調剤
            </button>
            <button
              onClick={() => setActiveTab('homecare')}
              className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'homecare'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover-bg-teal-50 hover:text-teal-600'
              }`}
            >
              在宅医療
            </button>
            <button
              onClick={() => setActiveTab('dx')}
              className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'dx'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover-bg-teal-50 hover:text-teal-600'
              }`}
            >
              DX・デジタル化
            </button>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {currentContent.map((item, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-8 border border-gray-100">
                <div className={`w-16 h-16 rounded-xl ${item.color} flex items-center justify-center mb-6`}>
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600 mb-6">{item.description}</p>
                <ul className="space-y-2">
                  {item.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">その他のリソース</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalResources.map((resource, index) => (
              <Link
                key={index}
                to={resource.link}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 group cursor-pointer"
              >
                <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${resource.icon} text-xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{resource.title}</h3>
                <p className="text-gray-600 text-sm">{resource.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            薬剤師の皆様へ
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            専門知識の向上と業務効率化をサポートいたします
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
              className="bg-teal-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              専門相談
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">専門薬局</h3>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => setActiveTab('specialty')} className="hover:text-white cursor-pointer">専門医療機関連携</button></li>
                <li><button onClick={() => setActiveTab('specialty')} className="hover:text-white cursor-pointer">地域連携薬局</button></li>
                <li><button onClick={() => setActiveTab('specialty')} className="hover:text-white cursor-pointer">健康サポート薬局</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">専門技術</h3>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => setActiveTab('sterile')} className="hover:text-white cursor-pointer">無菌調剤</button></li>
                <li><button onClick={() => setActiveTab('homecare')} className="hover:text-white cursor-pointer">在宅医療</button></li>
                <li><button onClick={() => setActiveTab('dx')} className="hover:text-white cursor-pointer">薬局DX</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/guidelines" className="hover:text-white cursor-pointer">疾患ガイドライン</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">薬剤師転職・派遣・キャリア情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
